import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { DASHBOARD_OMS, photos } from '../../content/media';

const services = [
  {
    id: 'it-managed-services',
    icon: 'ri-shield-check-line',
    title: 'IT Managed Services',
    description: 'We become your dedicated IT department — proactively monitoring, managing, and supporting your entire technology environment so you can focus on running your business.',
    features: [
      '24/7 Network & Systems Monitoring',
      'Help Desk & Technical Support',
      'Automated Patch Management',
      'IT Asset Lifecycle Management',
      'Performance Reporting & Analytics',
      'Vendor Relationship Management',
    ],
    image: photos.managedIt,
  },
  {
    id: 'cybersecurity-solutions',
    icon: 'ri-lock-2-line',
    title: 'Cybersecurity Solutions',
    description: 'Protect your business with enterprise-grade security that evolves alongside the threat landscape. From risk assessments to fully managed security operations, we have you covered.',
    features: [
      'Security Operations Center (SOC)',
      'Penetration Testing & Assessments',
      'Endpoint Detection & Response (EDR)',
      'Email Security & Phishing Defense',
      'Compliance & Regulatory Support',
      'Security Awareness Training',
    ],
    image: photos.cybersecurity,
  },
  {
    id: 'cloud-computing',
    icon: 'ri-cloud-line',
    title: 'Cloud Computing',
    description: 'Accelerate your cloud journey with expert-led strategy, seamless migration, and ongoing cloud management. We work with AWS, Azure, and Google Cloud to deliver optimal performance.',
    features: [
      'Cloud Readiness Assessment',
      'Migration Planning & Execution',
      'Multi-Cloud Architecture Design',
      'Cloud Security & Compliance',
      'FinOps & Cost Optimization',
      'Disaster Recovery as a Service',
    ],
    image: photos.cloud,
  },
  {
    id: 'it-consulting',
    icon: 'ri-lightbulb-line',
    title: 'IT Consulting',
    description: 'Our strategic consultants work alongside your leadership team to align technology investments with business objectives, driving efficiency and enabling digital transformation.',
    features: [
      'Technology Strategy & Roadmapping',
      'Digital Transformation Planning',
      'IT Architecture & Design',
      'Vendor Selection & Procurement',
      'IT Governance Frameworks',
      'M&A Technology Due Diligence',
    ],
    image: photos.consulting,
  },
  {
    id: 'it-infrastructure',
    icon: 'ri-server-line',
    title: 'IT Infrastructure Deployment',
    description: 'From network design to full data center buildouts, we design, procure, deploy, and document robust IT infrastructure that scales with your organization\'s growth.',
    features: [
      'Network Design & Implementation',
      'Server & Storage Solutions',
      'Data Center Build-Out',
      'Structured Cabling & Wireless',
      'Unified Communications (VoIP)',
      'Infrastructure Documentation',
    ],
    image: photos.infrastructure,
  },
  {
    id: 'custom-white-label-saas',
    icon: 'ri-code-s-slash-line',
    title: 'Custom White-Label SaaS Platform',
    description: 'We provide a fully customizable, white-label Software-as-a-Service (SaaS) platform designed to power critical business operations — including Human Resource Information Systems (HRIS), Order Management Systems (OMS), Logistics Management Systems (LMS), and additional modules tailored to your industry. This solution enables organizations to deploy enterprise-grade software under their own brand, eliminating the time, cost, and complexity of building proprietary systems from the ground up.',
    features: [
      'White-Label HRIS Module',
      'Order Management System (OMS)',
      'Logistics Management System (LMS)',
      'Industry-Tailored Custom Modules',
      'Enterprise-Grade Scalability',
      'Full Brand Identity Control',
    ],
    image: DASHBOARD_OMS,
  },
  {
    id: 'disaster-recovery',
    icon: 'ri-database-2-line',
    title: 'Disaster Recovery & Business Continuity',
    description: 'Ensure your business can withstand and rapidly recover from any disruption. Our DR solutions are tested, documented, and ready to activate when you need them most.',
    features: [
      'Business Impact Analysis (BIA)',
      'Backup Strategy & Implementation',
      'Recovery Time Objective (RTO) Planning',
      'Failover & Redundancy Design',
      'Regular DR Testing & Validation',
      'Business Continuity Documentation',
    ],
    image: photos.disasterRecovery,
  },
];

export default function Services() {
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0, 1, 2, 3, 5, 6]));
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 100);
        }
      };
      setTimeout(() => tryScroll(), 200);
    }
  }, [location.hash]);

  const isOpen = (i: number) => openSet.has(i);
  const toggleOpen = (i: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-white">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">What We Offer</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-3 tracking-[-0.03em]">Our IT Services</h1>
          <p className="text-page-muted text-base max-w-2xl mx-auto leading-relaxed font-light">
            Comprehensive technology solutions tailored to your business, from infrastructure to intelligence.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-8 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="space-y-6">
          {services.map((svc, i) => (
            <div
              key={svc.title}
              id={svc.id}
              className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-0 items-stretch rounded-3xl border border-black/[0.08] overflow-hidden hover:border-[#c97a2b]/30 transition-all duration-300 scroll-mt-24 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.05)]`}
            >
              {/* Image */}
              <div className="lg:w-2/5 w-full flex-shrink-0">
                <div className={`h-48 lg:h-full min-h-[200px] overflow-hidden flex items-center justify-center ${
                  svc.id === 'custom-white-label-saas' ? 'bg-page' : ''
                }`}>
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className={`w-full h-full ${
                      svc.id === 'custom-white-label-saas'
                        ? 'object-contain object-center'
                        : 'object-cover object-top'
                    }`}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 lg:p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#c97a2b]/10 border border-[#c97a2b]/25 mb-5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${svc.icon} text-[#c97a2b] text-sm`} />
                  </div>
                  <span className="text-xs font-bold text-[#c97a2b] uppercase tracking-wide">{svc.title}</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-extrabold text-page-ink mb-4 tracking-[-0.02em]">{svc.title}</h2>
                <p className="text-page-muted leading-relaxed mb-6 text-base">{svc.description}</p>

                <button
                  onClick={() => toggleOpen(i)}
                  className="flex items-center gap-2 text-sm font-bold mb-4 cursor-pointer text-[#c97a2b] hover:text-[#b06a22] transition-colors"
                >
                  <span>{isOpen(i) ? 'Hide Features' : 'View Features'}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${isOpen(i) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                  </div>
                </button>

                {isOpen(i) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 animate-fade-in">
                    {svc.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-sm text-[#c97a2b]" />
                        </div>
                        <span className="text-page-muted text-sm">{f}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Link
                  to="/contact"
                  className="btn-primary text-sm"
                >
                  Get a Quote <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
